import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import { logsAPI } from '../api/client';
import Layout from '../components/Layout';

const STATUS_BADGE = {
  success: 'bg-emerald-50 text-mint rounded-full px-2.5 py-0.5',
  failed: 'bg-coral/10 text-coral rounded-full px-2.5 py-0.5',
};

const STATUS_TEXT = {
  success: '성공',
  failed: '실패',
};

export default function Logs() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();

  const userId = user?.userId;
  const limit = 50;
  const [offset, setOffset] = useState(0);

  if (!isAuthenticated) {
    navigate('/login');
    return null;
  }

  const {
    data,
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ['logs', userId, offset],
    queryFn: async () => {
      const result = await logsAPI.get(userId, { limit, offset });
      if (result.success) {
        return { logs: result.logs || [], total: result.total || 0 };
      }
      throw new Error(result.error || '로그를 불러올 수 없습니다');
    },
    enabled: !!userId,
  });

  const logs = data?.logs || [];
  const total = data?.total || 0;
  const error = queryError ? (queryError.message || '로그를 불러올 수 없습니다') : null;

  const handlePrevPage = () => {
    if (offset > 0) {
      setOffset(Math.max(0, offset - limit));
    }
  };

  const handleNextPage = () => {
    if (offset + limit < total) {
      setOffset(offset + limit);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  // 통계 계산
  const stats = logs.reduce(
    (acc, log) => {
      if (log.status === 'success') {
        acc.success++;
      } else {
        acc.failed++;
      }
      return acc;
    },
    { success: 0, failed: 0 }
  );

  const successRate = logs.length > 0
    ? ((stats.success / logs.length) * 100).toFixed(1)
    : 0;

  if (!user) return null;

  return (
    <Layout title="예약 로그" backTo="/dashboard">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-tayo-light rounded-2xl p-4">
            <p className="text-sm text-sub-text mb-1">전체 기록</p>
            <p className="text-2xl font-extrabold text-tayo">{total}</p>
          </div>
          <div className="bg-emerald-50 rounded-2xl p-4">
            <p className="text-sm text-sub-text mb-1">성공</p>
            <p className="text-2xl font-extrabold text-mint">{stats.success}</p>
          </div>
          <div className="bg-coral/10 rounded-2xl p-4">
            <p className="text-sm text-sub-text mb-1">실패</p>
            <p className="text-2xl font-extrabold text-coral">{stats.failed}</p>
          </div>
          <div className="bg-violet-50 rounded-2xl p-4">
            <p className="text-sm text-sub-text mb-1">성공률</p>
            <p className="text-2xl font-extrabold text-violet-600">{successRate}%</p>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 bg-coral/10 border border-coral/20 rounded-xl p-4">
            <p className="text-sm text-coral">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-tayo"></div>
            <p className="mt-2 text-sub-text">로딩 중...</p>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && logs.length === 0 && (
          <div className="bg-white rounded-2xl shadow-card p-12 text-center">
            <p className="text-sub-text text-lg">예약 기록이 없습니다</p>
          </div>
        )}

        {!isLoading && logs.length > 0 && (
          <>
            {/* Desktop Table (lg and above) */}
            <div className="hidden lg:block bg-white rounded-2xl shadow-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-border">
                  <thead className="bg-snow">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-charcoal uppercase tracking-wider">
                        시간
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-charcoal uppercase tracking-wider">
                        상태
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-charcoal uppercase tracking-wider">
                        노선
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-charcoal uppercase tracking-wider">
                        버스 시간
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-charcoal uppercase tracking-wider">
                        좌석
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-charcoal uppercase tracking-wider">
                        소요시간
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-charcoal uppercase tracking-wider">
                        에러
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-border">
                    {logs.map((log) => (
                      <tr key={log.id} className="hover:bg-snow transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-charcoal">
                          {formatDate(log.executed_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex text-xs font-semibold ${
                              STATUS_BADGE[log.status] || 'bg-gray-100 text-gray-800 rounded-full px-2.5 py-0.5'
                            }`}
                          >
                            {STATUS_TEXT[log.status] || log.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-charcoal">
                          {log.route_name || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-charcoal">
                          {log.bus_time || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-charcoal">
                          {log.seat_no ? `${log.seat_no}번` : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-sub-text">
                          {log.metadata?.open_to_complete_ms
                            ? `${log.metadata.open_to_complete_ms}ms`
                            : log.execution_time_ms ? `${log.execution_time_ms}ms` : '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-sub-text max-w-xs truncate">
                          {log.error_message || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination (desktop) */}
              <div className="bg-white px-4 py-3 border-t border-border sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-sub-text">
                    <span className="font-medium text-charcoal">{offset + 1}</span>
                    {' - '}
                    <span className="font-medium text-charcoal">{Math.min(offset + limit, total)}</span>
                    {' / '}
                    <span className="font-medium text-charcoal">{total}</span>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={handlePrevPage}
                      disabled={offset === 0}
                      className="px-4 py-2 text-sm font-medium text-charcoal bg-gray-100 rounded-full hover:bg-gray-200 disabled:bg-gray-100 disabled:text-sub-text disabled:cursor-not-allowed transition-colors"
                    >
                      이전
                    </button>
                    <button
                      onClick={handleNextPage}
                      disabled={offset + limit >= total}
                      className="px-4 py-2 text-sm font-medium text-white bg-tayo rounded-full hover:bg-tayo-dark disabled:bg-gray-100 disabled:text-sub-text disabled:cursor-not-allowed transition-colors"
                    >
                      다음
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Card List (below lg) */}
            <div className="lg:hidden space-y-3">
              {logs.map((log) => (
                <div key={log.id} className="bg-white rounded-xl shadow-card p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-sub-text">{formatDate(log.executed_at)}</span>
                    <span
                      className={`inline-flex text-xs font-semibold ${
                        STATUS_BADGE[log.status] || 'bg-gray-100 text-gray-800 rounded-full px-2.5 py-0.5'
                      }`}
                    >
                      {STATUS_TEXT[log.status] || log.status}
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-sub-text">노선</span>
                      <span className="text-sm font-medium text-charcoal">{log.route_name || '-'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-sub-text">버스 시간</span>
                      <span className="text-sm font-medium text-charcoal">{log.bus_time || '-'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-sub-text">좌석</span>
                      <span className="text-sm font-medium text-charcoal">{log.seat_no ? `${log.seat_no}번` : '-'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-sub-text">소요시간</span>
                      <span className="text-sm text-sub-text">
                        {log.metadata?.open_to_complete_ms
                          ? `${log.metadata.open_to_complete_ms}ms`
                          : log.execution_time_ms ? `${log.execution_time_ms}ms` : '-'}
                      </span>
                    </div>
                    {log.error_message && (
                      <div className="pt-2 border-t border-border">
                        <span className="text-xs text-coral">{log.error_message}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* Pagination (mobile) */}
              <div className="flex items-center justify-between pt-2">
                <div className="text-sm text-sub-text">
                  <span className="font-medium text-charcoal">{offset + 1}</span>
                  {' - '}
                  <span className="font-medium text-charcoal">{Math.min(offset + limit, total)}</span>
                  {' / '}
                  <span className="font-medium text-charcoal">{total}</span>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={handlePrevPage}
                    disabled={offset === 0}
                    className="px-4 py-2 text-sm font-medium text-charcoal bg-gray-100 rounded-full hover:bg-gray-200 disabled:bg-gray-100 disabled:text-sub-text disabled:cursor-not-allowed transition-colors"
                  >
                    이전
                  </button>
                  <button
                    onClick={handleNextPage}
                    disabled={offset + limit >= total}
                    className="px-4 py-2 text-sm font-medium text-white bg-tayo rounded-full hover:bg-tayo-dark disabled:bg-gray-100 disabled:text-sub-text disabled:cursor-not-allowed transition-colors"
                  >
                    다음
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
    </Layout>
  );
}
